import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { createAxiosInstance } from "api/axiosConfig";

import {
  buildRecurrenceFields,
  validateAvailability,
} from "../utils/availabilityRecurrence";

const SEARCH_DEBOUNCE_MS = 400;
// One character is not worth a round trip; the backend filters on two or more.
const MIN_SEARCH_LENGTH = 2;

const todayIso = () => new Date().toISOString().split("T")[0];

export const emptyAvailability = () => ({
  staffMemberId: null,
  startTime: "09:00",
  endTime: "17:00",
  recurring: true,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  availableStartDate: todayIso(),
  availableEndDate: "",
});

/**
 * Staff availability list plus its create / update / delete calls.
 *
 * Search is server-side (`?name=`), debounced here so typing does not fire a
 * request per keystroke.
 */
export const useStaffAvailability = (tenantVal) => {
  const [availability, setAvailability] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchAvailability = useCallback(
    async (nameFilter = "") => {
      if (!tenantVal) return;

      try {
        setIsLoading(true);
        setIsError(false);
        const axiosInstance = createAxiosInstance();
        const query = nameFilter
          ? `?name=${encodeURIComponent(nameFilter)}`
          : "";
        const response = await axiosInstance.get(
          `${tenantVal}/staffavailability/getbyroster${query}`
        );

        setAvailability(response.data || []);
      } catch (error) {
        console.error("Error fetching staff availability:", error);
        setIsError(true);
        setAvailability([]);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantVal]
  );

  useEffect(() => {
    if (search.length > 0 && search.length < MIN_SEARCH_LENGTH) return;

    const timer = setTimeout(() => fetchAvailability(search), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search, fetchAvailability]);

  // Returns true when the record was saved, so callers can reset their form.
  const save = async (record) => {
    const validationError = validateAvailability(record);
    if (validationError) {
      toast.error(validationError);

      return false;
    }

    try {
      setIsSaving(true);
      const axiosInstance = createAxiosInstance();

      await axiosInstance.post(`${tenantVal}/staffavailability`, {
        ...(record.id ? { id: record.id } : {}),
        ...(record.staffMemberId
          ? { staffMemberId: Number(record.staffMemberId) }
          : {}),
        startTime: record.startTime,
        endTime: record.endTime,
        availableStartDate: record.availableStartDate,
        ...buildRecurrenceFields(record),
        tenant: parseInt(tenantVal, 10),
      });

      toast.success(
        record.id
          ? "Staff availability updated successfully"
          : "Staff availability created successfully"
      );
      await fetchAvailability(search);

      return true;
    } catch (error) {
      console.error("Error saving staff availability:", error);
      toast.error(
        record.id
          ? "Failed to update staff availability"
          : "Failed to create staff availability"
      );

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (record) => {
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.post(`${tenantVal}/staffavailability/delete`, record);
      toast.success("Staff availability deleted successfully");
      await fetchAvailability(search);
    } catch (error) {
      console.error("Error deleting staff availability:", error);
      toast.error("Failed to delete staff availability");
    }
  };

  return {
    availability,
    isLoading,
    isError,
    search,
    setSearch,
    isSaving,
    refetch: () => fetchAvailability(search),
    save,
    remove,
  };
};
